import { Paperclip, CreditCard, Check } from 'lucide-react';
import { formatDate, formatCurrency, formatHtTtc } from '../lib/format.js';
import { INTERNAL_QONTO_TYPES } from '../lib/transaction-utils.js';
import { formatRateFr } from '../lib/vat.js';
import { attachmentFileUrl } from '../hooks/useTransactionAttachments.js';
import EntityBadge from './EntityBadge.jsx';

/**
 * Ligne de transaction — responsive :
 * - Mobile (<md) : logo + (nom/date/cat/note) empilés + montant à droite
 * - Desktop (>=md) : colonnes fixes — logo | date | catégorie | nom | montant
 */

/**
 * Retourne true si la transaction est un débit avec une catégorie métier assignée —
 * condition nécessaire pour afficher le badge « TVA ? ».
 */
function shouldShowMissingVatBadge(tx) {
  // Une TVA à 0 validée (saisie ou justificatif) est renseignée. Un 0 posé par
  // défaut par Qonto ne l'est pas : il doit continuer à réclamer la TVA.
  if (tx.vat_source === 'manual' || tx.vat_source === 'inferred') return false;
  if (tx.amount_cents >= 0) return false;
  if (!tx.category_id && !tx.category_name) return false;
  if (INTERNAL_QONTO_TYPES.has(tx.operation_type)) return false;
  return true;
}

/**
 * Trombone : cliquable quand l'id du premier justificatif est connu (ouvre le proxy
 * Qonto dans un nouvel onglet), sinon simple indicateur. Le clic ne doit pas remonter
 * au bouton de ligne (qui ouvre le détail / l'édition de catégorie).
 */
function AttachmentBadge({ tx }) {
  if (!(tx.attachments_count > 0)) return null;
  if (!tx.first_attachment_id) {
    return <Paperclip size={12} className="shrink-0 text-slate-400" aria-label="Pièce jointe" />;
  }

  function openReceipt(e) {
    e.preventDefault();
    e.stopPropagation();
    window.open(attachmentFileUrl(tx.id, tx.first_attachment_id), '_blank', 'noopener,noreferrer');
  }

  return (
    <span
      role="link"
      tabIndex={0}
      title="Ouvrir le justificatif"
      aria-label="Ouvrir le justificatif"
      onClick={openReceipt}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openReceipt(e); }}
      className="shrink-0 rounded p-0.5 -m-0.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200 cursor-pointer"
    >
      <Paperclip size={12} />
    </span>
  );
}

/**
 * Sous-ligne TVA du montant. vat_amount_cents est signé comme amount_cents
 * (convention 0028) : négatif sur un débit, d'où la valeur absolue.
 */
/**
 * Infobulle TVA d'une ligne : « TVA 20% — source … » en taux unique, « TVA 20 % + 10 % —
 * source … » en multi-taux, ou « TVA — source … » quand le taux est inconnu (multi-taux
 * effacé côté backend, sans détail renvoyé).
 */
function vatTooltip(tx) {
  const breakdown = Array.isArray(tx.vat_breakdown) ? tx.vat_breakdown : [];
  const source = vatSourceLabel(tx.vat_source);
  if (breakdown.length >= 2) {
    const rates = breakdown.map((b) => `${formatRateFr(b.rate)} %`).join(' + ');
    return `TVA ${rates} — source ${source}`;
  }
  if (tx.vat_rate == null) {
    return `TVA — source ${source}`;
  }
  return `TVA ${formatRateFr(tx.vat_rate)}% — source ${source}`;
}

function VatLine({ tx, onClick }) {
  const vatCents = Math.abs(tx.vat_amount_cents ?? 0);
  if (vatCents > 0) {
    return (
      <p className="text-[10px] text-slate-400 tabular-nums" title={vatTooltip(tx)}>
        dont {formatCurrency(vatCents)} TVA
      </p>
    );
  }
  if (!shouldShowMissingVatBadge(tx)) return null;
  return (
    <p
      className="text-[10px] text-orange-500 hover:text-orange-700 cursor-pointer"
      onClick={(e) => { e.stopPropagation(); onClick?.(tx); }}
    >
      TVA ?
    </p>
  );
}

function vatSourceLabel(source) {
  if (source === 'qonto_auto') return 'auto (Qonto)';
  if (source === 'inferred') return 'auto (déduit)';
  if (source === 'manual') return 'manuelle';
  return source ?? '';
}

/**
 * Logo/avatar d'une transaction. En mode sélection multiple (`selectable`), le
 * logo devient la commande de sélection (role="checkbox") : cliquer dessus ne
 * doit pas ouvrir le détail de la ligne (stopPropagation), d'où l'utilisation
 * d'un <span> et non d'un <button> imbriqué dans le bouton de ligne.
 */
function LogoAvatar({ tx, displayName, sizeClass, selectable = false, selected = false, onToggleSelect }) {
  if (!selectable) {
    return tx.logo_url ? (
      <img
        src={tx.logo_url}
        alt=""
        className={`${sizeClass} rounded-full shrink-0 bg-slate-100 object-cover`}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    ) : (
      <div className={`${sizeClass} rounded-full shrink-0 bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500`}>
        {displayName.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  function toggle(e) {
    e.preventDefault();
    e.stopPropagation();
    onToggleSelect?.(tx);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect?.(tx);
    }
  }

  return (
    <span
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      aria-label={`Sélectionner ${displayName}`}
      title="Sélectionner"
      onClick={toggle}
      onKeyDown={onKeyDown}
      className={`${sizeClass} rounded-full shrink-0 inline-flex items-center justify-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-1 ${
        selected ? 'bg-slate-800' : 'hover:ring-2 hover:ring-slate-300 transition'
      }`}
    >
      {selected ? (
        <Check size={16} className="text-white" aria-hidden />
      ) : tx.logo_url ? (
        <img
          src={tx.logo_url}
          alt=""
          className="w-full h-full rounded-full bg-slate-100 object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
          {displayName.slice(0, 1).toUpperCase()}
        </div>
      )}
    </span>
  );
}

/**
 * @param {boolean} [showHtFormat=false] — quand true et que tx.vat_amount_cents est non nul,
 *   affiche "X € HT (Y € TTC)" au lieu du montant brut seul. Utilisé dans le drill-down
 *   cash-flow pour les transactions avec TVA connue.
 */
export default function TransactionRow({
  transaction,
  categories = [],
  onClick,
  compact = false,
  showHtFormat = false,
  selectable = false,
  selected = false,
  onToggleSelect,
}) {
  const tx = transaction;
  const isDebit = tx.side === 'debit' || tx.amount_cents < 0;
  const amount = Math.abs(tx.amount_cents ?? 0);

  const categoryName =
    tx.category_name ??
    categories.find((c) => c.id === tx.category_id)?.name ??
    null;

  const displayName =
    tx.clean_counterparty_name ??
    tx.counterparty_name ??
    tx.label ??
    '—';

  const subInfo = tx.qonto_cashflow_subcategory ?? tx.qonto_cashflow_category ?? tx.qonto_category ?? null;
  const dateText = formatDate(tx.settled_at ?? tx.operation_date);

  return (
    <button
      type="button"
      onClick={() => onClick?.(tx)}
      className={`w-full text-left hover:bg-slate-50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:bg-slate-50 ${
        selected ? 'bg-slate-100' : ''
      }`}
      aria-label={`Voir le détail de ${displayName}`}
    >
      {/* Layout mobile (<md) — empilé. Forcé sur tous breakpoints si compact=true. */}
      <div className={`${compact ? 'flex' : 'flex md:hidden'} items-center gap-3 px-4 py-3`}>
        <LogoAvatar
          tx={tx}
          displayName={displayName}
          sizeClass="w-9 h-9"
          selectable={selectable}
          selected={selected}
          onToggleSelect={onToggleSelect}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
            <AttachmentBadge tx={tx} />
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-slate-400">{dateText}</span>
            {categoryName && (
              <span className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded truncate max-w-[200px]">
                {categoryName}
              </span>
            )}
            {!categoryName && subInfo && (
              <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded truncate max-w-[200px]" title="Catégorie Qonto native">
                {subInfo}
              </span>
            )}
            {tx.status === 'pending' && (
              <span className="text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded">en attente</span>
            )}
            {tx.bank_provider === 'wise' && (
              <span className="text-[10px] bg-sky-50 text-sky-700 ring-1 ring-sky-200 px-1.5 py-0.5 rounded font-medium">Wise</span>
            )}
            {tx.intercompany_entity_id && (
              <span
                className="inline-flex items-center gap-1 text-[10px] bg-violet-50 text-violet-700 ring-1 ring-violet-200 px-1.5 py-0.5 rounded font-medium"
                title={`Flux intra-groupe avec ${tx.intercompany_entity_name ?? 'une société du groupe'}`}
              >
                Intra-groupe
                <EntityBadge slug={tx.intercompany_entity_slug} name={tx.intercompany_entity_name} className="bg-violet-100 ring-violet-200 text-violet-700" />
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {showHtFormat && tx.vat_amount_cents ? (
            /* Mode HT/TTC : "X € HT (Y € TTC)" */
            <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
              {isDebit ? '-' : ''}{formatHtTtc(Math.abs(tx.amount_cents ?? 0), Math.abs(tx.vat_amount_cents))}
            </span>
          ) : (
            <>
              <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
                {isDebit ? '-' : '+'}{formatCurrency(amount)}
              </span>
              <VatLine tx={tx} onClick={onClick} />
            </>
          )}
        </div>
      </div>

      {/* Layout desktop (>=md) — colonnes : logo | date | fournisseur | catégorie | montant.
          Masqué quand compact=true (on reste sur l'empilé quelque soit le breakpoint). */}
      <div className={`${compact ? 'hidden' : 'hidden md:grid'} md:grid-cols-[40px_100px_minmax(180px,1.5fr)_minmax(160px,1fr)_140px] md:gap-4 md:items-center md:px-4 md:py-2.5`}>
        {/* Colonne 1 : logo */}
        <div className="shrink-0">
          <LogoAvatar
            tx={tx}
            displayName={displayName}
            sizeClass="w-8 h-8"
            selectable={selectable}
            selected={selected}
            onToggleSelect={onToggleSelect}
          />
        </div>

        {/* Colonne 2 : date */}
        <div className="text-xs text-slate-500 tabular-nums">
          {dateText}
          {tx.status === 'pending' && (
            <span className="block text-[10px] text-yellow-700">en attente</span>
          )}
        </div>

        {/* Colonne 3 : fournisseur (nom + méta note, attachment, card) */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-slate-800 truncate">{displayName}</p>
            <AttachmentBadge tx={tx} />
            {tx.operation_type === 'card' && tx.card_last_digits && (
              <span className="text-[10px] text-slate-400 shrink-0 inline-flex items-center gap-0.5">
                <CreditCard size={10} />•{tx.card_last_digits}
              </span>
            )}
          </div>
          {tx.note && (
            <p className="text-xs text-slate-400 italic truncate">« {tx.note} »</p>
          )}
        </div>

        {/* Colonne 4 : catégorie */}
        <div className="min-w-0">
          {categoryName ? (
            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded truncate inline-block max-w-full">
              {categoryName}
            </span>
          ) : subInfo ? (
            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded truncate inline-block max-w-full" title="Catégorie Qonto">
              {subInfo}
            </span>
          ) : (
            <span className="text-xs text-slate-300 italic">Non classifiée</span>
          )}
        </div>

        {/* Colonne 5 : montant + montant TVA sous le total */}
        <div className="text-right">
          {showHtFormat && tx.vat_amount_cents ? (
            <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
              {isDebit ? '-' : ''}{formatHtTtc(Math.abs(tx.amount_cents ?? 0), Math.abs(tx.vat_amount_cents))}
            </span>
          ) : (
            <>
              <span className={`text-sm font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
                {isDebit ? '-' : '+'}{formatCurrency(amount)}
              </span>
              <VatLine tx={tx} onClick={onClick} />
            </>
          )}
        </div>
      </div>
    </button>
  );
}
